import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { UserService } from "src/app/services/user.service";
import { environment } from "../../../environments/environment";
import {
    CompanySearchParams,
    defaultCompanySearchParams,
} from "../../data-types/search-params";
import { AlertService } from "../../services/alert.service";
import { CompanyService } from "../../services/company.service";
import { OverlayService } from "../../services/overlay.service";
import { ProductsService } from "../../services/products.service";
import { Store } from "../../store";

@Component({
    selector: "app-product-range-edit",
    templateUrl: "./product-range-edit.component.html",
    styleUrls: ["./product-range-edit.component.scss"],
})
export class ProductRangeEditComponent implements OnInit {
    env = environment;
    user$ = this.store.select<any>("user");

    form: FormGroup;
    uploadConfig: any;
    resizeWidth: 500;
    newItem = true;
    primaryRanges = [];
    productTypes = [];
    data;
    suppliers = [];
    domains = [];
    domain;

    maxTileText = 75;
    maxBannerText = 300;

    overlays$ = this.store.select<any>("overlays");
    overlayData;
    standardPricing = [];
    showImageLibrary = false;
    imageLibraryType = "";
    imageDataField = "";
    collectionAddresses: any;
    showCollectionAddress: boolean = false;

    constructor(
        private store: Store,
        private overlayService: OverlayService,
        private router: Router,
        private route: ActivatedRoute,
        private productsService: ProductsService,
        private companyService: CompanyService,
        private fb: FormBuilder,
        private alertService: AlertService,
        private userService: UserService
    ) {
        this.form = this.fb.group({
            name: ["", [Validators.required, Validators.minLength(3)]],
            supplier: [null, [Validators.required]],
            tileText: [
                "",
                [Validators.required, Validators.maxLength(this.maxTileText)],
            ],
            bannerText: [
                "",
                [Validators.required, Validators.maxLength(this.maxBannerText)],
            ],
            slug: ["", [Validators.required, Validators.minLength(3)]],
            domains: [[], Validators.required],
            bespokeImageUrl: ["", []],
            imageUrl: ["", []],
            bannerUrl: ["", []],
            showInMainMenu: [false, [Validators.required]],
            showInPreviewSlider: [false, [Validators.required]],
            seoTitle: ["", [Validators.required]],
            seoDescription: ["", [Validators.required]],
            available: [1, []],
            collectionAddress: ["", []],
        });
    }

    ngOnInit(): void {
        this.domains = this.store.selectForLocal("domains");
        if (this.route.snapshot.params.slug === "new") {
            this.newItem = true;
            this.data = {
                name: "",
                tileText: "",
                bannerText: "",
                slug: "",
                supplier: "",
                level: 0,
                parent: 0,
                productTypes: [],
                imageUrl: "",
                bespokeImageUrl: "",
                bannerUrl: "",
                showInMainMenu: false,
                showInPreviewSlider: false,
                seoTitle: "",
                seoDescription: "",
                standardPricing: [],
                available: 1,
                collectionAddress: "",
            };
        } else {
            this.newItem = false;
            this.productsService
                .getProductRangeBySlug(this.route.snapshot.params.slug)
                .subscribe((data) => {
                    this.data = data.data;
                    this.standardPricing = data.data.standardPricing;
                    if (this.data.supplier != "") {
                        this.showCollectionAddress = true;
                    }

                    const domains = [];

                    for (let d = 0; d < this.data.domains.length; d++) {
                        domains.push(this.data.domains[d]);
                    }

                    this.form.patchValue({
                        name: this.data.name,
                        tileText: this.data.tileText,
                        bannerText: this.data.bannerText,
                        supplier: this.data.supplier,
                        slug: this.data.slug,
                        domains,
                        imageUrl: this.data.imageUrl,
                        bespokeImageUrl: this.data.bespokeImageUrl,
                        bannerUrl: this.data.bannerUrl,
                        available: this.data.available,
                        parent: this.data.parent,
                        showInMainMenu: this.data.showInMainMenu,
                        showInPreviewSlider: this.data.showInPreviewSlider,
                        seoTitle: this.data.seoTitle,
                        seoDescription: this.data.seoDescription,
                        collectionAddress: this.data.collectionAddress,
                    });
                });
        }
        this.getProductTypes();
        this.findAll();
        this.initUpload();
        this.getSuppliers();
    }

    getSuppliers() {
        const params = { ...defaultCompanySearchParams } as CompanySearchParams;
        this.companyService.search(params).subscribe((data) => {
            this.suppliers = data.data.data;
            if (this.route.snapshot.params.slug != "new") {
                this.getCollectionAddress(this.data.supplier);
            }
        });
    }

    togglePreviewSlider() {
        if (!this.form.value.showInPreviewSlider) {
            this.form.patchValue({ showInPreviewSlider: true });
        } else {
            this.form.patchValue({ showInPreviewSlider: false });
        }
    }
    toggleMainMenu() {
        if (!this.form.value.showInMainMenu) {
            this.form.patchValue({ showInMainMenu: true });
        } else {
            this.form.patchValue({ showInMainMenu: false });
        }
    }
    toggleAvailable() {
        if (!this.form.value.available) {
            this.form.patchValue({ available: true });
        } else {
            this.form.patchValue({ available: false });
        }
    }

    getProductTypes() {
        this.productsService
            .getProductTypes({ status: true })
            .subscribe((data) => {
                this.productTypes = data.data;
            });
    }

    productTypeSelected(id) {
        for (let i = 0; i < this.data.productTypes.length; i++) {
            if (this.data.productTypes[i].id === id) {
                return i;
            }
        }
        return -1;
    }

    productTypeBespokeAllowed(id) {
        for (let i = 0; i < this.data.productTypes.length; i++) {
            if (this.data.productTypes[i].id === id) {
                if (this.data.productTypes[i].allowBespoke) {
                    return true;
                } else {
                    return false;
                }
            }
        }
        return false;
    }

    toggleProductTypes(productType) {
        const currentIndex = this.productTypeSelected(productType.id);
        if (currentIndex >= 0) {
            this.data.productTypes.splice(currentIndex, 1);
        } else {
            this.data.productTypes.push(productType);
        }
    }

    toggleProductTypeBespoke(pt) {
        const currentIndex = this.productTypeSelected(pt.id);
        if (currentIndex >= 0) {
            if (!this.data.productTypes[currentIndex].allowBespoke) {
                this.data.productTypes[currentIndex].allowBespoke = 1;
            } else {
                this.data.productTypes[currentIndex].allowBespoke = 0;
            }
        }
    }

    findAll() {
        this.productsService.getProductRanges().subscribe((data) => {
            this.primaryRanges = data.data;
        });
    }

    onSubmit() {
        this.data.name = this.form.value.name;
        this.data.tileText = this.form.value.tileText;
        this.data.bannerText = this.form.value.bannerText;
        this.data.slug = this.form.value.slug;
        this.data.imageUrl = this.form.value.imageUrl;
        this.data.bespokeImageUrl = this.form.value.bespokeImageUrl;
        this.data.bannerUrl = this.form.value.bannerUrl;
        this.data.available = this.form.value.available;
        this.data.parent = this.form.value.parent;
        this.data.showInMainMenu = this.form.value.showInMainMenu;
        this.data.supplier = this.form.value.supplier;
        this.data.showInPreviewSlider = this.form.value.showInPreviewSlider;
        this.data.seoTitle = this.form.value.seoTitle;
        this.data.seoDescription = this.form.value.seoDescription;
        this.data.domains = this.form.value.domains;
        this.data.collectionAddress = this.form.value.collectionAddress;

        if (this.data.parent > 0) {
            this.data.level = 1;
        }

        if (this.newItem) {
            this.productsService
                .createProductRange(this.data)
                .subscribe((data) => {
                    this.alertService.notification(
                        ["Your product range has been created"],
                        3000
                    );
                    this.router.navigate(["/product-ranges"]);
                });
        } else {
            this.productsService
                .updateProductRange(this.data)
                .subscribe((data) => {
                    this.alertService.notification(
                        ["Your product range has been updated"],
                        3000
                    );
                    // this.router.navigate(['/product-ranges']);
                });
        }
    }

    initUpload() {
        this.uploadConfig = {
            url: this.env.imageUploadPath + "products/upload/image",
            acceptedFiles: "image/*",
            parallelUploads: 1,
            resizeWidth: this.resizeWidth,
            maxFiles: 1,
            maxFilesize: 5120,
            // The API's upload routes now require the admin session: send the cookie and
            // the app-name the API uses to pick auth.app.admin (Dropzone bypasses the interceptor).
            withCredentials: true,
            headers: {
                "api-key": "6bf9284a-ebf9-4afa-a185-4d2b9bb29e85",
                "app-name": "admin",
            },
        };
    }

    onUploadError(e) {
        this.initUpload();
    }

    onUploadSuccess(e) {
        this.form.patchValue({
            imageUrl: e[1].data,
        });
        this.initUpload();
    }

    uploadprogress(e) {}

    deleteImage() {
        this.form.patchValue({ imageUrl: "" });
    }

    editProductType(pt) {
        this.overlayData = {
            productType: pt,
            range: this.data,
        };
        this.overlayService.toggle("productTypeConfigure");
    }

    // banner image

    imageSelected(e) {
        if (this.imageDataField === "bespokeTile") {
            this.form.value.bespokeImageUrl = e.url;
        }
        if (this.imageDataField === "tile") {
            this.form.value.imageUrl = e.url;
        }
        if (this.imageDataField === "introBackground") {
            this.form.value.bannerUrl = e.url;
        }
        this.showImageLibrary = false;
    }
    libraryModalAction(e) {
        this.showImageLibrary = false;
    }

    viewImageLibrary(type, imageDataField) {
        this.imageLibraryType = type;
        this.imageDataField = imageDataField;
        this.showImageLibrary = true;
    }

    deleteTile() {
        this.form.value.imageUrl = "";
    }

    deleteIntroBackground() {
        this.form.value.bannerUrl = "";
    }

    onItemSelect(e) {}
    onDomainAdd(e) {}
    onDomainRemove(e) {}

    findInvalidControls() {
        const invalid = [];
        const controls = this.form.controls;
        for (const name in controls) {
            if (controls[name].invalid) {
                invalid.push(name);
            }
        }
        return invalid;
    }

    supplierChange(supplierId) {
        this.form.controls["collectionAddress"].reset();
        this.showCollectionAddress = false;
        if (supplierId != "") {
            this.showCollectionAddress = true;
            this.getCollectionAddress(supplierId);
        }
    }

    getCollectionAddress(supplierId) {
        const supplierData = this.suppliers.find(
            (supplier) => supplier.id == supplierId
        );
        if (supplierData) {
            this.userService
                .findAddressesByCustomer(supplierData.primaryUserId)
                .subscribe((data: any) => {
                    this.collectionAddresses = data.data.filter(
                        (item) => item.add1 != ""
                    );
                });
        }
    }
}
